import type { App, InjectionKey } from 'vue';
import { inject } from 'vue';
import { sourceReferencesApi, type SourceReferencesApi } from '@/apis/source-references';
import type {
    TypedCommandExecutor,
    TypedCommandRegistrar,
} from '@/services/commands/typed-command';
import { createApplicationCommandRegistry } from '@/services/commands/application-command-registry';
import { SET_CHAT_SELECTION_CONTEXT } from '@/services/commands/application-commands/set-chat-selection-context';
import {
    InMemoryChatSelectionContextService,
    type ChatSelectionContextService,
} from '@/services/chat/chat-selection-context-service';
import {
    InMemoryWriteDiffCoordinator,
    type WriteDiffCoordinator,
} from '@/services/document/write-diff-coordinator';
import { attachmentEvents } from '@/services/events/attachment-events';
import { chatEvents } from '@/services/events/chat-events';
import { documentEvents } from '@/services/events/document-events';
import { documentModificationEvents } from '@/services/events/document-modification-events';
import { oarEvents } from '@/services/events/oar-events';
import { projectEvents } from '@/services/events/project-events';
import { toolEvents } from '@/services/events/tool-events';
import type { AttachmentEventPublisher, AttachmentEventSource } from '@/services/events/attachment-events';
import type { ChatEventPublisher, ChatEventSource } from '@/services/events/chat-events';
import type { DocumentEventPublisher, DocumentEventSource } from '@/services/events/document-events';
import type {
    DocumentModificationEventPublisher,
    DocumentModificationEventSource,
} from '@/services/events/document-modification-events';
import type { OarEventPublisher, OarEventSource } from '@/services/events/oar-events';
import type { ProjectEventPublisher, ProjectEventSource } from '@/services/events/project-events';
import type { ToolEventPublisher, ToolEventSource } from '@/services/events/tool-events';

/** 表现层可访问的应用服务门面。具体实现只在本模块选择。 */
export interface ApplicationServices {
    sourceReferences: SourceReferencesApi;
    commandExecutor: TypedCommandExecutor;
    commandRegistrar: TypedCommandRegistrar;
    chatSelectionContextService: ChatSelectionContextService;
    writeDiffCoordinator: WriteDiffCoordinator;
    attachmentEventSource: AttachmentEventSource;
    attachmentEventPublisher: AttachmentEventPublisher;
    chatEventSource: ChatEventSource;
    chatEventPublisher: ChatEventPublisher;
    documentEventSource: DocumentEventSource;
    documentEventPublisher: DocumentEventPublisher;
    documentModificationEventSource: DocumentModificationEventSource;
    documentModificationEventPublisher: DocumentModificationEventPublisher;
    oarEventSource: OarEventSource;
    oarEventPublisher: OarEventPublisher;
    projectEventSource: ProjectEventSource;
    projectEventPublisher: ProjectEventPublisher;
    toolEventSource: ToolEventSource;
    toolEventPublisher: ToolEventPublisher;
}

type CommandRegistryService = TypedCommandExecutor & TypedCommandRegistrar;

export interface ApplicationServicesOverrides extends Partial<
    Omit<
        ApplicationServices,
        'commandExecutor' | 'commandRegistrar' | 'chatSelectionContextService'
        | 'writeDiffCoordinator'
    >
> {
    commandRegistry?: CommandRegistryService;
    chatSelectionContextService?: ChatSelectionContextService;
    writeDiffCoordinator?: WriteDiffCoordinator;
}

/** 显式装配应用服务，便于测试替换依赖。 */
export function createApplicationServices(
    overrides: ApplicationServicesOverrides = {},
): ApplicationServices {
    const commandRegistry = overrides.commandRegistry ?? createApplicationCommandRegistry();
    const chatSelectionContextService = overrides.chatSelectionContextService
        ?? new InMemoryChatSelectionContextService();
    const writeDiffCoordinator = overrides.writeDiffCoordinator
        ?? new InMemoryWriteDiffCoordinator();

    commandRegistry.registerCommand(SET_CHAT_SELECTION_CONTEXT, {
        execute: request => chatSelectionContextService.set({
            text: request.text ?? '',
            dataIds: request.dataIds ?? [],
            document: request.document,
        }),
    });

    return {
        sourceReferences: overrides.sourceReferences ?? sourceReferencesApi,
        commandExecutor: commandRegistry,
        commandRegistrar: commandRegistry,
        chatSelectionContextService,
        writeDiffCoordinator,
        attachmentEventSource: overrides.attachmentEventSource ?? attachmentEvents,
        attachmentEventPublisher: overrides.attachmentEventPublisher ?? attachmentEvents,
        chatEventSource: overrides.chatEventSource ?? chatEvents,
        chatEventPublisher: overrides.chatEventPublisher ?? chatEvents,
        documentEventSource: overrides.documentEventSource ?? documentEvents,
        documentEventPublisher: overrides.documentEventPublisher ?? documentEvents,
        documentModificationEventSource: overrides.documentModificationEventSource ?? documentModificationEvents,
        documentModificationEventPublisher: overrides.documentModificationEventPublisher ?? documentModificationEvents,
        oarEventSource: overrides.oarEventSource ?? oarEvents,
        oarEventPublisher: overrides.oarEventPublisher ?? oarEvents,
        projectEventSource: overrides.projectEventSource ?? projectEvents,
        projectEventPublisher: overrides.projectEventPublisher ?? projectEvents,
        toolEventSource: overrides.toolEventSource ?? toolEvents,
        toolEventPublisher: overrides.toolEventPublisher ?? toolEvents,
    };
}

export const applicationServicesKey: InjectionKey<ApplicationServices> = Symbol('ApplicationServices');

/** 将已装配服务提供给 Vue 表现层。 */
export function provideApplicationServices(app: App, services: ApplicationServices): void {
    app.provide(applicationServicesKey, services);
}

/** 仅供 Vue 表现层获取应用服务门面。 */
export function useApplicationServices(): ApplicationServices {
    const services = inject(applicationServicesKey);
    if (!services) {
        throw new Error('ApplicationServices 尚未在应用 composition root 中装配');
    }
    return services;
}
